// leaflet_map.js
import L from "leaflet";
import * as bootstrap from "bootstrap";

// ——— helpers ———
function humanDuration(seconds) {
  if (seconds == null) return "-";
  const s = Math.round(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (!h && !m) return "0m";
  return `${h ? `${h}h ` : ""}${m}m`.trim();
}

// tenta usar window.polyline se você incluiu via <script>; se usa bundler, pode trocar por: import polyline from "@mapbox/polyline";
const decodePolyline6 = (polylineStr) => {
  try {
    if (window.polyline && typeof window.polyline.decode === "function") {
      return window.polyline.decode(polylineStr, 6); // [[lat,lng],...]
    }
  } catch (_) {}
  return null;
};

// índice do ponto no polyline mais próximo de um alvo (lat/lng)
function closestPointIndex(pointsLatLng, targetLat, targetLng) {
  let best = 0, bestD = Infinity;
  for (let i = 0; i < pointsLatLng.length; i++) {
    const [lat, lng] = pointsLatLng[i];
    const dlat = lat - targetLat;
    const dlng = lng - targetLng;
    const d = dlat * dlat + dlng * dlng;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

// 🔹 abre offcanvas com pedidos da parada
function openStopOrdersOffcanvas(routeStopId, stopLabel = "") {
  const title = document.getElementById("stopOrdersCanvasLabel");
  const body  = document.getElementById("stopOrdersCanvasBody");
  if (title && stopLabel) title.textContent = `Pedidos da Parada ${stopLabel}`;
  if (body) body.innerHTML = `<div class="text-muted">Carregando...</div>`;

  fetch(`/tower/orders_for_stop?route_stop_id=${routeStopId}`, {
    headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" },
  })
    .then((r) => r.text())
    .then((html) => {
      if (body) body.innerHTML = html;
      const off = bootstrap.Offcanvas.getOrCreateInstance(document.getElementById("stopOrdersCanvas"));
      off.show();
    })
    .catch((e) => {
      console.error(e);
      if (body) body.innerHTML = `<div class="text-danger">Erro ao carregar pedidos da parada.</div>`;
      const off = bootstrap.Offcanvas.getOrCreateInstance(document.getElementById("stopOrdersCanvas"));
      off.show();
    });
}

function stopSequenceLabel(stop, totalStops = 0) {
  const stopOrder = Number(stop?.stop_order);
  const stopsTotal = Number(totalStops);
  if (!Number.isFinite(stopOrder) || stopOrder <= 0) return "-";
  if (!Number.isFinite(stopsTotal) || stopsTotal <= 0) return String(stopOrder);
  return `${stopOrder}/${stopsTotal}`;
}

export function initMap() {
  const mapElement = document.getElementById("vehicle-map");
  if (!mapElement) return;

  const latitude = Number(mapElement.dataset.lat);
  const longitude = Number(mapElement.dataset.lng);

  const map = L.map("vehicle-map").setView([latitude, longitude], 13);

  L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
    attribution: '&copy; <a href="https://entregui.com.br/">Entregui</a>',
    subdomains: "abcd",
    maxZoom: 19,
  }).addTo(map);

  let activeMarker = null;
  const vehicleMarkers = {};
  const routeLayerGroup = L.layerGroup().addTo(map); // camada para desenhar a rota clicada

  // helper: limpa rota e seleção atual
  function clearRouteAndSelection() {
    routeLayerGroup.clearLayers();
    if (activeMarker) {
      activeMarker.getElement()?.classList.remove('highlight');
      activeMarker = null;
    }
    const vcEl = document.getElementById('vehicleCanvas');
    const vc = bootstrap.Offcanvas.getInstance(vcEl);
    vc?.hide();
    const scEl = document.getElementById('stopOrdersCanvas');
    const sc = scEl ? bootstrap.Offcanvas.getInstance(scEl) : null;
    sc?.hide();
  }

  // clicar no “chão” do mapa limpa a rota
  map.on('click', () => clearRouteAndSelection());

  // ========= (1) Carregar SOMENTE veículos com rota IN_ROUTE =========
  fetch("/tower/active_vehicles")
    .then((r) => r.json())
    .then((vehicles) => {
      if (vehicles.length > 0) {
        const bounds = L.latLngBounds(vehicles.map((v) => [v.last_latitude, v.last_longitude]));
        map.fitBounds(bounds.pad(0.1));
      }

      vehicles.forEach((vehicle) => {
        const truckIcon = L.divIcon({
          html: `<i class="bi bi-truck" style="font-size: 24px; color: #0F3544;"></i>`,
          className: "leaflet-div-icon truck-icon",
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        const marker = L.marker([vehicle.last_latitude, vehicle.last_longitude], { icon: truckIcon }).addTo(map);

        // ========= (2) Clique: buscar rota ativa e desenhar trajeto =========
        marker.addEventListener("click", async () => {
          if (activeMarker) activeMarker.getElement()?.classList.remove("highlight");
          marker.getElement()?.classList.add("highlight");
          activeMarker = marker;

          routeLayerGroup.clearLayers();

          // busca rota do back
          let payload;
          try {
            const res = await fetch(`/tower/route_for_vehicle?vehicle_id=${vehicle.id}`, {
              headers: { Accept: "application/json" },
            });
            payload = await res.json();
          } catch (e) {
            payload = null;
          }

          // Offcanvas sempre mostra algo — com ou sem rota
          if (!payload || !payload.route) {
            fillVehicleOffcanvas({ vehicle, route: null });
            bootstrap.Offcanvas.getOrCreateInstance(document.getElementById("vehicleCanvas")).show();
            return;
          }

          const r = payload.route;

          // warehouse (se houver)
          if (r.warehouse?.latitude && r.warehouse?.longitude) {
            const wh = L.marker([r.warehouse.latitude, r.warehouse.longitude], {
              icon: L.icon({
                iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
                shadowSize: [41, 41],
              }),
            }).bindPopup("Warehouse");
            routeLayerGroup.addLayer(wh);
          }

          // paradas numeradas (com clique para pedidos)
          const totalStops = Array.isArray(r.stops) ? r.stops.length : 0;
          const stopsBounds = [];
          (r.stops || []).forEach((s) => {
            const lat = Number(s.latitude);
            const lng = Number(s.longitude);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return;

            const color = s.status === "COMPLETED" ? "#00cc00" : "#1E90FF";
            const stopIcon = L.divIcon({
              className: "custom-stop-icon",
              html: `<div style="background:${color};color:#fff;width:24px;height:24px;border-radius:50%;
                           text-align:center;line-height:24px;font-weight:700;">${s.stop_order}</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
              popupAnchor: [0, -12],
            });

            const mk = L.marker([lat, lng], { icon: stopIcon });
            mk.on("click", () => openStopOrdersOffcanvas(s.id, stopSequenceLabel(s, totalStops)));

            routeLayerGroup.addLayer(mk);
            stopsBounds.push([lat, lng]);
          });

          // ====== LINHA OSRM BICOLOR (COMPLETED 🟢 / IN_PROGRESS 🔵) ======
          let decodedAll = null;

          // 1) converte GeoJSON (LineString) OU polyline6 em [[lat,lng], ...]
          if (r.osrm_route) {
            try {
              const feature = Array.isArray(r.osrm_route.features)
                ? r.osrm_route.features.find(f => f.geometry && f.geometry.type === 'LineString')
                : (r.osrm_route.geometry && r.osrm_route.geometry.type === 'LineString' ? r.osrm_route : null);

              if (feature) {
                const coords = (feature.geometry || feature).coordinates; // [lng,lat]
                decodedAll = coords.map(([lng, lat]) => [lat, lng]);
              }
            } catch (_) {}
          }
          if (!decodedAll && r.osrm_polyline) {
            decodedAll = decodePolyline6(r.osrm_polyline);
          }

          if (!decodedAll || !decodedAll.length) {
            // fallback: linha única azul
            if (r.osrm_route) {
              try {
                routeLayerGroup.addLayer(L.geoJSON(r.osrm_route, { color: "#1E90FF", weight: 4, opacity: 0.8 }));
              } catch (_) {}
            } else if (r.osrm_polyline) {
              const d = decodePolyline6(r.osrm_polyline);
              if (d && d.length) {
                routeLayerGroup.addLayer(L.polyline(d, { color: "#1E90FF", weight: 4, opacity: 0.8 }));
              }
            }
          } else {
            // 2) identificar última parada COMPLETED
            const stopsSorted = (r.stops || []).slice().sort((a,b) => (a.stop_order||0) - (b.stop_order||0));
            const lastCompleted = stopsSorted.filter(s => s.status === 'COMPLETED').pop();

            if (!lastCompleted) {
              // ninguém concluído → tudo azul
              routeLayerGroup.addLayer(L.polyline(decodedAll, { color: "#1E90FF", weight: 4, opacity: 0.9 }));
            } else {
              // 3) ponto do polyline mais próximo da última COMPLETED
              const splitIdx = closestPointIndex(
                decodedAll,
                Number(lastCompleted.latitude),
                Number(lastCompleted.longitude)
              );

              // 4) fatia em "feito" e "a fazer"
              const a = Math.max(1, splitIdx + 1);
              const doneSlice = decodedAll.slice(0, a);
              const todoSlice = decodedAll.slice(Math.max(0, splitIdx), decodedAll.length);

              // 5) desenha bicolor
              routeLayerGroup.addLayer(L.polyline(doneSlice, { color: "#00cc00", weight: 4, opacity: 0.9 })); // 🟢 COMPLETED
              routeLayerGroup.addLayer(L.polyline(todoSlice, { color: "#1E90FF", weight: 4, opacity: 0.9 }));   // 🔵 IN_PROGRESS
            }
          }
          // ====== FIM LINHA BICOLOR ======

          // enquadra
          try {
            // se desenhamos polylines acima, use o último adicionado para pegar bounds
            // aqui usamos os bounds das paradas como fallback
            if (decodedAll && decodedAll.length) {
              const lineBounds = L.latLngBounds(decodedAll);
              map.fitBounds(lineBounds, { padding: [20, 20] });
            } else if (stopsBounds.length) {
              map.fitBounds(L.latLngBounds(stopsBounds), { padding: [20, 20] });
            } else {
              map.flyTo([vehicle.last_latitude, vehicle.last_longitude], 15);
            }
          } catch (_) {}

          // offcanvas com resumo + link pro show
          fillVehicleOffcanvas({ vehicle, route: r });
          bootstrap.Offcanvas.getOrCreateInstance(document.getElementById("vehicleCanvas")).show();
        });

        vehicleMarkers[vehicle.id] = { marker, vehicle: { ...vehicle } };
      });
    });

  // expõe pra debugar se precisar
  window.__map = map;
  window.__vehicleMarkers = vehicleMarkers;
}

// ——— offcanvas do veículo ———
function fillVehicleOffcanvas({ vehicle, route }) {
  const title = route ? `${vehicle.name} — Rota #${route.route_number}` : vehicle.name;
  document.getElementById("vehicleCanvasLabel").innerText = title;

  if (!route) {
    document.getElementById("vehicleCanvasBody").innerHTML = `
      <p><strong>ID:</strong> ${vehicle.id}</p>
      <p><strong>Latitude:</strong> ${Number(vehicle.last_latitude || 0).toFixed(6)}</p>
      <p><strong>Longitude:</strong> ${Number(vehicle.last_longitude || 0).toFixed(6)}</p>
      <p class="text-muted mb-0">Sem rota ativa para este veículo.</p>
    `;
    return;
  }

  const dist = route.est_distance_km != null ? `${Number(route.est_distance_km).toFixed(2)} km` : "-";
  const dur  = route.est_duration_s != null ? humanDuration(route.est_duration_s) : "-";
  const start= route.start_time ? new Date(route.start_time).toLocaleString() : "-";

  document.getElementById("vehicleCanvasBody").innerHTML = `
    <strong>Detalhes da Rota</strong>
    <hr>
    <p class="mb-2"><strong>Rota:</strong> ${route.route_number}</p>
    <p class="mb-2"><strong>Motorista: </strong> ${route.driver}</p>
    <p class="mb-2"><strong>Número de Paradas:</strong> ${route.stops_count}</p>
    <p class="mb-2"><strong>Número de Pedidos:</strong> ${route.orders_count}</p>
    <p class="mb-2"><strong>Status:</strong> ${route.status}</p>
    <p class="mb-2"><strong>Início:</strong> ${start}</p>
    <p class="mb-2"><strong>Distância estimada:</strong> ${dist}</p>
    <p class="mb-3"><strong>Duração estimada:</strong> ${dur}</p>
    <p class="mb-3"><strong>Anotação:</strong> -</p>
    <hr>
    <div class="d-grid gap-2">
      <a class="btn btn-sm btn-outline-primary" href="/routes/${route.id}" target="_blank">
        Abrir detalhes da rota
      </a>
    </div>
  `;
}

// ——— offcanvas ROTAS (mantido) ———
async function loadRoutesIntoOffcanvas() {
  const body = document.getElementById("routesCanvasBody");
  const badge = document.getElementById("routesBadge");
  if (!body) return;

  body.innerHTML = `<div class="text-muted">Carregando...</div>`;

  try {
    const res = await fetch("/tower/routes_in_route", {
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
    });
    const html = await res.text();
    body.innerHTML = html;

    const count = body.querySelectorAll(".card[data-route-id]").length;
    if (badge) badge.textContent = String(count);
  } catch (e) {
    console.error("Erro carregando rotas:", e);
    body.innerHTML = `<div class="text-danger">Erro ao carregar rotas.</div>`;
  }
}

export function wireRoutesButton() {
  const routesBtn = document.getElementById("btn-routes");
  const routesCanvas = document.getElementById("routesCanvas");
  if (!routesBtn || !routesCanvas) return;

  routesBtn.addEventListener("click", async () => {
    await loadRoutesIntoOffcanvas();
    const off = bootstrap.Offcanvas.getOrCreateInstance(routesCanvas);
    off.show();
  });
}
